/*
 * Copyright (c) 2017-present, flop! contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 * https://framagit.org/flopedt/flopedt
 */

import { useStorage } from '@vueuse/core'

import { i18n } from './index.ts'
import { DEFAULT_LOCALE, loadMessages, SUPPORTED_LOCALES } from './messages.ts'

/** @enum */
type TimeFormatValue = (typeof TimeFormat)[keyof typeof TimeFormat]
const TimeFormat = {
  DEFAULT: 'DEFAULT',
  HOUR_12: 'HOUR_12',
  HOUR_24: 'HOUR_24',
} as const

const LANGUAGE_STORAGE_KEY = 'i18n-locale'
const TIME_FORMAT_STORAGE_KEY = 'i18n-time-format'

export function getPreferredLocale() {
  // 1. Configured locale in-app
  const persistedLocale = localStorage.getItem(LANGUAGE_STORAGE_KEY)
  if (persistedLocale && SUPPORTED_LOCALES.includes(persistedLocale)) {
    return persistedLocale
  }

  // 2. Configured locale in the browser
  // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/languages
  for (const locale of navigator.languages) {
    if (SUPPORTED_LOCALES.includes(locale)) return locale

    const dash = locale.indexOf('-')
    if (dash > 0) {
      const localeWithoutRegion = locale.slice(0, dash)
      if (SUPPORTED_LOCALES.includes(localeWithoutRegion)) return localeWithoutRegion
    }
  }

  // 3. Fallback locale
  return DEFAULT_LOCALE
}

export async function changeLocale(locale: string) {
  // Load messages if necessary
  if (!i18n.global.availableLocales.includes(locale)) {
    const messages = await loadMessages(locale)
    i18n.global.setLocaleMessage(locale, messages)
  }

  // Set the locale
  i18n.global.locale.value = locale
  document.documentElement.setAttribute('lang', locale)
  document.documentElement.setAttribute('dir', i18n.global.t('_meta.dir'))

  // Persist to localstorage for later use
  localStorage.setItem(LANGUAGE_STORAGE_KEY, locale)
}

export const timeFormat = useStorage<TimeFormatValue>(TIME_FORMAT_STORAGE_KEY, TimeFormat.DEFAULT)

export function initSettings() {
  const locale = getPreferredLocale()
  if (locale !== DEFAULT_LOCALE) void changeLocale(locale)
}
